import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, CreditCard, Smartphone, Loader2, ShieldCheck, CheckCircle2, ArrowRight, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const OPERATORS = [
  { id: "airtel", label: "Airtel Money", color: "#E4001B" },
  { id: "orange", label: "Orange Money", color: "#FF7900" },
  { id: "mpesa", label: "M-Pesa", color: "#00A651" },
];

export default function DonationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState({ donations: [], cotisations: [] });
  const [subPackages, setSubPackages] = useState([]);
  const [tab, setTab] = useState("don");
  const [selected, setSelected] = useState(null);
  const [method, setMethod] = useState("stripe");
  const [operator, setOperator] = useState("airtel");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [interval, setInterval_] = useState("month");

  // Mobile Money OTP step
  const [mmStep, setMmStep] = useState("form"); // form | otp
  const [mmTx, setMmTx] = useState(null);
  const [otp, setOtp] = useState("");

  useEffect(() => {
    api.get("/donations/packages").then((r) => {
      setPackages(r.data);
      if (r.data.donations?.[2]) setSelected(r.data.donations[2].id);
    });
    api.get("/subscriptions/packages").then((r) => setSubPackages(r.data || []));
  }, []);

  useEffect(() => {
    if (tab === "subscription") {
      const filtered = subPackages.filter((p) => p.interval === interval);
      if (filtered.length) setSelected(filtered[1]?.id || filtered[0].id);
    } else {
      const list = tab === "don" ? packages.donations : packages.cotisations;
      if (list?.length) setSelected(list[Math.min(1, list.length - 1)].id);
    }
    setMmStep("form"); setMmTx(null); setOtp("");
  }, [tab, packages, subPackages, interval]);

  const list = tab === "subscription"
    ? subPackages.filter((p) => p.interval === interval)
    : (tab === "don" ? packages.donations : packages.cotisations);
  const selPkg = list?.find((p) => p.id === selected);
  const selOperator = OPERATORS.find((o) => o.id === operator);

  const handleSubscribe = async () => {
    if (!user) { toast.error("Connectez-vous pour souscrire à un abonnement."); navigate("/login"); return; }
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/create", {
        package_id: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe");
      setLoading(false);
    }
  };

  const handleStripe = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        package_id: selected,
        origin_url: window.location.origin,
        purpose: tab,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setLoading(false);
    }
  };

  const handleMobileInitiate = async () => {
    if (!user) { toast.error("Connectez-vous pour utiliser Mobile Money."); navigate("/login"); return; }
    if (!phone || phone.length < 9) { toast.error("Numéro invalide."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/donations/mobile-money/initiate", {
        package_id: selected, phone, operator, purpose: tab,
      });
      setMmTx(data);
      setMmStep("otp");
      toast.success(data.message);
      // Demo only: auto-prefill OTP
      if (data.otp_demo) setOtp(data.otp_demo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setLoading(false);
    }
  };

  const handleMobileConfirm = async () => {
    if (!otp || otp.length < 4) { toast.error("Code OTP invalide"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/donations/mobile-money/confirm", {
        transaction_id: mmTx.transaction_id, otp,
      });
      toast.success(data.message);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="donations-page" className="min-h-screen">
      <section className="relative py-20 md:py-28 text-white overflow-hidden bg-gradient-to-br from-[#0A0F1A] via-[#0052CC] to-[#0A0F1A]">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <FourStars size={14} className="justify-center mb-4" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Dons & Cotisations</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-tight">Bâtissons ensemble le Congo de demain</h1>
          <p className="text-white/80 mt-5 max-w-2xl mx-auto leading-relaxed">
            Votre contribution finance des actions concrètes : éducation, santé, soutien à l'Est, mobilisation citoyenne.
          </p>
        </div>
      </section>

      <section className="py-16 stars-pattern">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="flex justify-center mb-10" data-testid="donation-tabs">
            <div className="inline-flex rounded-md bg-white shadow-sm border border-black/5 p-1">
              {[
                { id: "don", label: "Don ponctuel" },
                { id: "cotisation", label: "Cotisation membre" },
                { id: "subscription", label: "Abonnement récurrent" },
              ].map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                  className={`px-5 py-2.5 text-sm font-semibold rounded-sm transition-all ${tab === t.id ? "bg-[#0052CC] text-white shadow" : "text-[#475569] hover:text-[#0052CC]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-[#D4AF37]/20 p-8 md:p-12">
            {/* Mobile Money OTP step */}
            {mmStep === "otp" && mmTx ? (
              <div data-testid="mm-otp-step">
                <button onClick={() => { setMmStep("form"); setOtp(""); setMmTx(null); }} className="text-sm text-[#475569] hover:text-[#0052CC] mb-4">← Retour</button>
                <div className="text-center max-w-md mx-auto">
                  <div className="inline-flex w-16 h-16 rounded-full items-center justify-center mb-5" style={{ backgroundColor: `${selOperator.color}15` }}>
                    <Smartphone size={28} style={{ color: selOperator.color }} />
                  </div>
                  <h2 className="font-display text-2xl text-[#0A0F1A]">Confirmation {selOperator.label}</h2>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">
                    Un code de confirmation a été envoyé au <strong>+{mmTx.phone}</strong>.
                    Saisissez-le pour confirmer votre paiement de <strong>${mmTx.amount}</strong>.
                  </p>
                  {mmTx.otp_demo && (
                    <div className="mt-4 px-3 py-2 rounded bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-xs text-[#0A0F1A]">
                      <strong className="uppercase">Mode démo :</strong> code OTP = <span className="font-mono">{mmTx.otp_demo}</span>
                    </div>
                  )}
                  <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    autoFocus
                    data-testid="mm-otp-input"
                    className="w-full mt-6 text-center font-mono text-2xl tracking-[0.5em] py-4 rounded-md border-2 border-black/10 focus:border-[#D4AF37] focus:outline-none bg-[#FAFAFA]"
                  />
                  <Button
                    onClick={handleMobileConfirm}
                    disabled={loading || otp.length < 6}
                    className="cta-glow-blue rounded-sm py-6 w-full mt-5 font-semibold uppercase tracking-wider"
                    data-testid="mm-confirm-btn"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Lock size={16} className="mr-2" />}
                    Confirmer le paiement
                  </Button>
                  <p className="text-[10px] text-[#475569] mt-3 leading-relaxed">
                    Production : intégration native via API Airtel Money / Orange Money / Vodacom M-Pesa.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl md:text-3xl text-[#0A0F1A]">
                  {tab === "don" ? "Choisissez le montant de votre don"
                    : tab === "cotisation" ? "Choisissez votre cotisation membre"
                    : "Souscrivez à un abonnement récurrent"}
                </h2>

                {tab === "subscription" && (
                  <div className="mt-5 inline-flex rounded-md bg-[#FAFAFA] border border-black/5 p-1" data-testid="interval-toggle">
                    {[
                      { id: "month", label: "Mensuel" },
                      { id: "year", label: "Annuel" },
                    ].map((iv) => (
                      <button
                        key={iv.id}
                        onClick={() => setInterval_(iv.id)}
                        data-testid={`interval-${iv.id}`}
                        className={`px-5 py-2 text-sm rounded-sm transition-all ${interval === iv.id ? "bg-[#D4AF37] text-[#0A0F1A] font-semibold" : "text-[#475569] hover:text-[#0052CC]"}`}
                      >
                        {iv.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-7" data-testid="package-grid">
                  {list.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelected(p.id)}
                      data-testid={`package-${p.id}`}
                      className={`relative rounded-lg border-2 p-5 text-left transition-all ${selected === p.id ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-md" : "border-black/10 hover:border-[#0052CC]/40 bg-white"}`}
                    >
                      {selected === p.id && <CheckCircle2 size={18} className="absolute top-3 right-3 text-[#D4AF37]" fill="#D4AF37" stroke="white" />}
                      <div className="font-display text-3xl text-[#0A0F1A]">${p.amount}</div>
                      <div className="text-xs uppercase tracking-[0.15em] text-[#475569] mt-1">{p.label}</div>
                      {tab === "subscription" && <div className="text-[10px] text-[#0052CC] mt-1.5 font-semibold">/ {p.interval === "month" ? "mois" : "an"}</div>}
                    </button>
                  ))}
                </div>

                <StarsDivider className="my-10" />

                {tab !== "subscription" && (
                  <>
                    <h3 className="font-display text-xl text-[#0A0F1A] mb-4">Choisissez votre moyen de paiement</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payment-methods">
                  <button onClick={() => setMethod("stripe")} data-testid="method-stripe"
                    className={`flex items-center gap-3 p-5 rounded-lg border-2 transition ${method === "stripe" ? "border-[#0052CC] bg-[#0052CC]/5" : "border-black/10 hover:border-[#0052CC]/30"}`}>
                    <CreditCard size={22} className="text-[#0052CC]" />
                    <div className="text-left">
                      <div className="font-semibold text-sm text-[#0A0F1A]">Carte bancaire</div>
                      <div className="text-xs text-[#475569]">Visa, MasterCard via Stripe</div>
                    </div>
                  </button>
                  <button onClick={() => setMethod("mobile")} data-testid="method-mobile"
                    className={`flex items-center gap-3 p-5 rounded-lg border-2 transition ${method === "mobile" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-black/10 hover:border-[#D4AF37]/40"}`}>
                    <Smartphone size={22} className="text-[#D4AF37]" />
                    <div className="text-left">
                      <div className="font-semibold text-sm text-[#0A0F1A]">Mobile Money RDC</div>
                      <div className="text-xs text-[#475569]">Airtel · Orange · M-Pesa</div>
                    </div>
                  </button>
                </div>

                {method === "mobile" && tab !== "subscription" && (
                  <div className="mt-6 p-5 bg-[#FAFAFA] rounded-lg border border-black/5" data-testid="mobile-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                      {OPERATORS.map((o) => (
                        <button key={o.id} onClick={() => setOperator(o.id)} data-testid={`operator-${o.id}`}
                          className={`py-2.5 rounded-md text-sm font-medium border-2 transition ${operator === o.id ? "border-[var(--op)] bg-[color-mix(in_srgb,var(--op)_15%,white)] text-[#0A0F1A]" : "border-black/10 text-[#475569]"}`}
                          style={{ "--op": o.color }}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                    <input type="tel" placeholder="+243 81 234 5678" value={phone} onChange={(e) => setPhone(e.target.value)}
                      data-testid="mobile-phone-input"
                      className="w-full px-4 py-3 rounded-md border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                  </div>
                )}

                  </>
                )}

                <div className="flex items-center gap-2 text-xs text-[#475569] mt-6">
                  <ShieldCheck size={14} className="text-[#0052CC]" /> Paiements 100% sécurisés. Données chiffrées HTTPS.
                </div>

                <Button
                  size="lg"
                  disabled={loading || !selected}
                  onClick={tab === "subscription" ? handleSubscribe : (method === "stripe" ? handleStripe : handleMobileInitiate)}
                  data-testid="confirm-payment-btn"
                  className="w-full mt-6 cta-glow-blue rounded-sm py-6 text-base font-bold uppercase tracking-wider"
                >
                  {loading ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Heart size={18} className="mr-2" />}
                  {tab === "subscription" && selPkg
                    ? `S'abonner pour $${selPkg.amount}/${selPkg.interval === "month" ? "mois" : "an"}`
                    : selPkg
                      ? `Payer $${selPkg.amount} — ${selPkg.label}`
                      : "Choisissez un montant"}
                  {method === "mobile" && tab !== "subscription" && selPkg && <ArrowRight size={16} className="ml-2" />}
                </Button>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
